// src/services/invoiceService.js
import * as XLSX from 'xlsx';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const getHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

// Don't set Content-Type when sending FormData (for file uploads)
const getFileHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return {
    'Authorization': `Bearer ${token}`
  };
};

const invoiceService = {
  getList: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (filters.company) queryParams.append('company', filters.company);
      if (filters.service_type) queryParams.append('service_type', filters.service_type);
      if (filters.date_from) queryParams.append('date_from', filters.date_from);
      if (filters.date_to) queryParams.append('date_to', filters.date_to);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.paid) queryParams.append('paid', filters.paid);
      if (filters.payment_type) queryParams.append('payment_type', filters.payment_type);
      if (filters.search) queryParams.append('search', filters.search);
      
      const response = await fetch(`${BASE_URL}/invoices/?${queryParams.toString()}`, {
        method: 'GET',
        headers: getHeaders()
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error fetching invoices:', errorText);
        throw new Error(`Failed to fetch invoices: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error in getList:', error);
      throw error;
    }
  },
  
  getById: async (id) => {
    try {
      const response = await fetch(`${BASE_URL}/invoices/${id}/`, {
        method: 'GET',
        headers: getHeaders()
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Error fetching invoice ${id}:`, errorText);
        throw new Error(`Failed to fetch invoice: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`Error in getById (${id}):`, error);
      throw error;
    }
  },
  
  create: async (invoiceData) => {
    try {
      // Ensure total_amount has max 2 decimal places
      if (invoiceData.total_amount !== undefined && invoiceData.total_amount !== null) {
        invoiceData.total_amount = parseFloat(parseFloat(invoiceData.total_amount).toFixed(2));
      }
      const response = await fetch(`${BASE_URL}/invoices/`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(invoiceData)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error creating invoice:', errorText);
        throw new Error(`Failed to create invoice: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error in create:', error);
      throw error;
    }
  },
  
  createWithFile: async (formData) => {
    try {
      // Log the FormData for debugging
      /*console.log('FormData contents:'); 
      for (let [key, value] of formData.entries()) {
        console.log(`${key}: ${value instanceof File ? value.name : value}`);
      } */
      
      
      // Ensure total_amount has max 2 decimal places
      if (formData.has("total_amount")) {
        const totalAmount = parseFloat(formData.get("total_amount"));
        if (!isNaN(totalAmount)) {
          formData.set("total_amount", parseFloat(totalAmount.toFixed(2)).toString());
        }
      }
      const response = await fetch(`${BASE_URL}/invoices/`, {
        method: 'POST',
        headers: getFileHeaders(), // Don't set Content-Type for FormData
        body: formData
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error creating invoice with file:', errorText);
        throw new Error(`Failed to create invoice with file: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error in createWithFile:', error);
      throw error;
    }
  },
  
  update: async (id, invoiceData) => {
    try {
      // Ensure total_amount has max 2 decimal places
      if (invoiceData.total_amount !== undefined && invoiceData.total_amount !== null) {
        invoiceData.total_amount = parseFloat(parseFloat(invoiceData.total_amount).toFixed(2));
      }
      const response = await fetch(`${BASE_URL}/invoices/${id}/`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(invoiceData)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Error updating invoice ${id}:`, errorText);
        throw new Error(`Failed to update invoice: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`Error in update (${id}):`, error);
      throw error;
    }
  },
  
  updateWithFile: async (id, formData) => {
    try {
		/* console.log(`Updating invoice ${id} with FormData`); */
      
      // Make sure 'method' is correctly set in formData if it's needed by your backend
      if (!formData.has('_method')) {
        formData.append('_method', 'PUT');
      }
      
      // Ensure total_amount has max 2 decimal places
      if (formData.has("total_amount")) {
        const totalAmount = parseFloat(formData.get("total_amount"));
        if (!isNaN(totalAmount)) {
          formData.set("total_amount", parseFloat(totalAmount.toFixed(2)).toString());
        }
      }
      
      // Use PATCH instead of PUT for FormData to handle partial updates with files
      const response = await fetch(`${BASE_URL}/invoices/${id}/`, {
        method: 'PATCH',
        headers: getFileHeaders(),
        body: formData
      });
      
      if (!response.ok) {
        // First try to parse as JSON
        let errorData;
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          errorData = await response.json();
        } else {
          errorData = await response.text();
        }
        console.error(`Error updating invoice ${id} with file:`, errorData);
        throw new Error(`Failed to update invoice: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`Error in updateWithFile (${id}):`, error);
      throw error;
    }
  },
  
  delete: async (id) => {
    try {
      const response = await fetch(`${BASE_URL}/invoices/${id}/`, {
        method: 'DELETE',
        headers: getHeaders()
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Error deleting invoice ${id}:`, errorText);
        throw new Error(`Failed to delete invoice: ${response.status}`);
      }
      
      return true;
    } catch (error) {
      console.error(`Error in delete (${id}):`, error);
      throw error;
    }
  },
  
  // Add transaction to an invoice
  addTransaction: async (id, formData) => {
    try {
      // Log the FormData for debugging
	  /* console.log('Transaction FormData contents:');
      for (let [key, value] of formData.entries()) {
        console.log(`${key}: ${value instanceof File ? value.name : value}`);
      } */
      
      const response = await fetch(`${BASE_URL}/invoices/${id}/add_transaction/`, {
        method: 'POST',
        headers: getFileHeaders(), // Don't set Content-Type for FormData
        body: formData
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Error adding transaction to invoice ${id}:`, errorText);
        throw new Error(`Failed to add transaction: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`Error in addTransaction (${id}):`, error);
      throw error;
    }
  },
  
  exportToExcel: (invoices) => {
    try {
      // Create a new workbook
      const wb = XLSX.utils.book_new();
      
      // Convert invoices to worksheet format
      const wsData = invoices.map(invoice => ({
        ID: invoice.id,
        'Invoice Number': invoice.invoice_number,
        Company: invoice.company,
        Date: invoice.date ? new Date(invoice.date).toLocaleDateString() : '',
        'Service Type': invoice.service_type,
        Quantity: invoice.quantity,
        Price: invoice.price,
		'Total Amount GEL': invoice.total_amountGel,
        'Total Amount AMD': invoice.total_amount,
        Paid: invoice.paid ? 'Yes' : 'No',
        'Payment Type': invoice.payment_type,
        Status: invoice.status
      }));
      
      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(wsData);
      
      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Invoices');
      
      // Save to file
      const fileName = `invoices_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      
      return true;
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      throw error;
    }
  }
};

export default invoiceService;